import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { Save, Recycle } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function BOMMatrix() {
  const [models, setModels] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [links, setLinks] = useState({});
  const [selectedModel, setSelectedModel] = useState("");
  const [matrix, setMatrix] = useState({}); // key: process_id → row
  const [saving, setSaving] = useState("");

  const loadBom = useCallback(async (model) => {
    if (!model) return;
    const { data } = await api.get("/bom", { params: { model_name: model } });
    const map = {};
    for (const r of data) map[r.process_id] = r;
    setMatrix(map);
  }, []);

  useEffect(() => {
    Promise.all([api.get("/producible-items"), api.get("/processes"), api.get("/model-process-links")]).then(([m, p, l]) => {
      setModels(m.data); setProcesses(p.data); setLinks(l.data);
      if (m.data.length > 0) setSelectedModel(m.data[0].name);
    });
  }, []);

  useEffect(() => { loadBom(selectedModel); }, [selectedModel, loadBom]);

  // Only the processes linked to this model (route). If none linked yet -> all processes.
  const routeIds = links[selectedModel];
  const visibleProcesses = routeIds && routeIds.length ? processes.filter((p) => routeIds.includes(p.id)) : processes;

  const updateCell = (pid, field, val) => {
    setMatrix((prev) => ({ ...prev, [pid]: { ...(prev[pid] || {}), [field]: val } }));
  };

  const saveRow = async (pid) => {
    const row = matrix[pid] || {};
    setSaving(pid);
    try {
      await api.post("/bom", {
        model_name: selectedModel, process_id: pid,
        cumulative_weight_g: +(row.cumulative_weight_g || 0), // preserved (set elsewhere)
        scrap_weight_g: +(row.scrap_weight_g || 0),
        labor_rate: +(row.labor_rate || 0),
        consumable_cost: +(row.consumable_cost || 0),
      });
      toast.success("Scrap row saved");
      loadBom(selectedModel);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(""); }
  };

  return (
    <Layout title="Process Scrap" subtitle="Per-model, per-process mandatory scrap weight — feeds the Scrap Tracker">
      <Toaster richColors position="top-right" />
      <div className="section-card mb-4">
        <label className="block text-sm font-medium text-slate-700 mb-2">Select Semi-FG / Model</label>
        <select data-testid="bom-model-select" value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)} className="w-full md:w-96 border border-slate-300 rounded-lg px-3 py-2 text-sm">
          {models.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
        </select>
        <p className="text-xs text-slate-500 mt-2">Enter the scrap that each process generates for this model. Mandatory scrap in the tracker = pieces produced (DPR) × scrap weight/pc.</p>
      </div>

      <div className="section-card overflow-x-auto">
        <div className="flex items-center gap-2 mb-3"><Recycle size={18} className="text-emerald-600" /><h3 className="font-heading font-semibold">{selectedModel || "—"}</h3></div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left py-3 px-3">Process</th>
              <th className="text-right py-3 px-3">Scrap Wt (g/pc)</th>
              <th className="text-right py-3 px-3">Labor ₹/pc</th>
              <th className="text-right py-3 px-3">Consumable ₹/pc</th>
              <th></th>
            </tr>
          </thead>
          <tbody data-testid="scrap-table-body">
            {visibleProcesses.map((p) => {
              const row = matrix[p.id] || {};
              return (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/40">
                  <td className="py-2 px-3 font-medium">{p.name} <span className="text-xs text-slate-400">[{p.phase}]</span></td>
                  <td className="py-1 px-3"><input data-testid={`bom-scrap-${p.id}`} type="number" step="0.01" value={row.scrap_weight_g ?? ""} onChange={(e) => updateCell(p.id, "scrap_weight_g", e.target.value)} className="w-28 ml-auto block border border-slate-300 rounded px-2 py-1 text-right text-sm" /></td>
                  <td className="py-1 px-3"><input data-testid={`bom-labor-${p.id}`} type="number" step="0.01" value={row.labor_rate ?? ""} onChange={(e) => updateCell(p.id, "labor_rate", e.target.value)} className="w-24 ml-auto block border border-slate-300 rounded px-2 py-1 text-right text-sm" /></td>
                  <td className="py-1 px-3"><input data-testid={`bom-cons-${p.id}`} type="number" step="0.01" value={row.consumable_cost ?? ""} onChange={(e) => updateCell(p.id, "consumable_cost", e.target.value)} className="w-24 ml-auto block border border-slate-300 rounded px-2 py-1 text-right text-sm" /></td>
                  <td className="py-1 px-3 text-right"><button data-testid={`bom-save-${p.id}`} disabled={saving === p.id} onClick={() => saveRow(p.id)} className="bg-blue-600 hover:bg-blue-700 text-white p-1.5 rounded disabled:opacity-40"><Save size={14} /></button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="text-xs text-slate-500 mt-3">Scrap weight is in grams per piece. Save each row individually.</p>
      </div>
    </Layout>
  );
}
